import { useEffect, useRef, type ReactNode } from 'react';

interface Props {
  title: string;
  children: ReactNode;
  confirmText?: string;
  cancelText?: string;
  tone?: 'danger' | 'normal';
  onConfirm?: () => void;
  onClose: () => void;
  wide?: boolean;
  confirmDisabled?: boolean;
}

/** Модальный диалог: Enter — подтвердить, Esc — закрыть. */
export function Dialog({ title, children, confirmText, cancelText = 'Отмена', tone = 'normal', onConfirm, onClose, wide, confirmDisabled }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
      if (e.key === 'Enter' && onConfirm && !confirmDisabled) {
        e.preventDefault();
        e.stopPropagation();
        onConfirm();
      }
    };
    window.addEventListener('keydown', onKey, true);
    ref.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, onConfirm, confirmDisabled]);
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`dialog ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <header>
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </header>
        <div className="dialog-body">{children}</div>
        {onConfirm && (
          <footer>
            <button className="btn ghost" onClick={onClose}>
              {cancelText}
            </button>
            <button className={`btn ${tone === 'danger' ? 'danger' : 'primary'}`} onClick={onConfirm} disabled={confirmDisabled} data-autofocus>
              {confirmText ?? 'Подтвердить'}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}
